<template>
  <div class="ms-layout">
    <div v-if="isLoading" class="spinner-overlay">
      <div class="spinner-border" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>

    <div v-if="!isLoading" class="container-fluid">
      <div class="row ms-bottom-border NKT-P1-medium">
        <div></div>
        <div class="col">Spare Name</div>
        <div class="col">Earliest Expiry</div>
        <div class="col">Location</div>
        <div class="col">Total quantity</div>
      </div>

      <div v-for="(elm, index) in filterSparePartGroup(inventory)" :key="index" class="row pt-2">
        <div class="col">
          <div class="accordion" :id="'spare-parts-accordion-' + index">
            <div class="accordion-item NKT-P1">
              <div
                class="d-flex justify-content-between align-items-center ms-accordion-item-header sticky-top"
                :id="'spare-parts-accordion-label-' + index"
                data-bs-toggle="collapse"
                :data-bs-target="'#spare-parts-accordion-collapse' + index"
                :aria-expanded="false"
                :aria-controls="'spare-parts-accordion-collapse' + index"
                @click="toggleExpanded(index)"
              >
                <div class="col-icon">
                  <img
                    :src="arrow"
                    class="ms-rotate-icon"
                    :class="{ rotated: isExpanded(index) }"
                  />
                </div>
                <div class="col">
                  {{ elm.spareName }}
                </div>
                <div class="col">
                  <span :class="{ 'expired-date-cell': elm.isExpired }">
                    {{ elm.earliestExpiry ? formatTimestamp(elm.earliestExpiry) : 'No Limit' }}
                  </span>
                </div>
                <div class="col">
                  {{ elm.location }}
                </div>
                <div class="col">
                  {{ sumofSparePartsQuantity(elm.spareParts) }}
                </div>
              </div>
              <div
                :id="'spare-parts-accordion-collapse' + index"
                class="accordion-collapse collapse"
                :aria-labelledby="'spare-parts-accordion-label-' + index"
                :data-bs-parent="'spare-parts-accordion-' + index"
              >
                <div class="accordion-body">
                  <SparePartsTable
                    class="mt-4"
                    :isLoading="isLoading"
                    :spareparts="filteredSparePart(elm.spareParts)"
                    :idindex="index"
                  ></SparePartsTable>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import arrow from '@/assets/svg/arrow.svg'
import { SparePart, SparePartGroups } from '@/server/MakeSenseApp-backend'
import moment from 'moment'
import { computed, defineProps, ref, watch } from 'vue'
import SparePartsTable from './SparePartsTable.vue'

var props = defineProps({
  inventory: Array<SparePartGroups>,
  isLoading: Boolean,
  searchValue: String
})

const toggleExpanded = (index: number) => {
  if (expandedItems.value.has(index)) {
    expandedItems.value.delete(index)
  } else {
    expandedItems.value.add(index)
  }
}

const filterSparePartGroup = (sparePartGroups: SparePartGroups[] | undefined) => {
  if (sparePartGroups === undefined) {
    return []
  }
  return sparePartGroups.filter((sparePartGroup) => {
    return filteredSparePart(sparePartGroup.spareParts)?.length !== 0
  })
}

const sumofSparePartsQuantity = (spareParts: SparePart[] | undefined): number => {
  if (!spareParts) return 0

  return spareParts.reduce((total, part) => {
    const qty = Number(part.quantity)
    return total + (isNaN(qty) ? 0 : qty)
  }, 0)
}

const filteredSparePart = (spareParts: SparePart[] | undefined) => {
  if (spareParts === undefined) {
    return []
  }

  return spareParts.filter((sparePart) => {
    return (
      sparePart.subcategory?.toLowerCase().includes(props.searchValue?.toLowerCase() ?? '') ||
      sparePart.spareName?.toLowerCase().includes(props.searchValue?.toLowerCase() ?? '') ||
      sparePart.identity?.toLowerCase().includes(props.searchValue?.toLowerCase() ?? '') ||
      sparePart.description?.toLowerCase().includes(props.searchValue?.toLowerCase() ?? '') ||
      sparePart.storageDescription?.toLowerCase().includes(props.searchValue?.toLowerCase() ?? '')
    )
  })
}

const formatTimestamp = (timestamp: Date | undefined): string => {
  if (!timestamp) return '-'
  return moment(timestamp).format('YYYY-MM-DD')
}

const expandedItems = ref(new Set<number>())

const isExpanded = (index: number) => {
  return expandedItems.value.has(index)
}

// Computed value to track when displayed groups actually change
const displayedGroups = computed(() => filterSparePartGroup(props.inventory))

// Clear expanded state only when displayed content changes
watch(displayedGroups, () => {
  expandedItems.value.clear()
})
</script>

<style scoped lang="scss">
@use '@/styles/ms-color-styles.scss' as color;

.col-icon {
  width: 24px;
}

.spinner-overlay {
  position: absolute;
  top: 30%;
  left: 50%;
  z-index: 10;
}

.ms-bottom-border {
  border-bottom: 1px solid;
}

.ms-rotate-icon {
  transition: transform 0.3s ease;
  transform-origin: center;
}

.ms-rotate-icon.rotated {
  transform: rotate(180deg);
}

.ms-layout {
  padding: var(--horizontal-gap-medium, 24px) var(--general-side-margin-wrapper, 16px)
    var(--horizontal-gap-large, 48px) var(--general-side-margin-wrapper, 16px);
  overflow: auto;
  position: relative;
  z-index: 0;
  background: color.$color-white;
}

.accordion-body {
  padding: 0;
}

.accordion-item {
  border-radius: 0%;
  border: none;
}

.ms-accordion-item-header {
  height: 42px;
  cursor: pointer;
}

.ms-accordion-item-header:hover {
  background-color: color.$color-lighter-grey;
}

.col {
  padding-left: 0%;
  padding-right: 0%;

  h4 {
    margin-bottom: 0px;
    padding-bottom: 0px;
  }
}

.expired-date-cell {
  background-color: #ffd4bb;
}

.sticky-header {
  position: sticky;
  top: 0;
  z-index: 1; /* Ensure it stays on top of accordion rows */
  background-color: white; /* Prevent transparency when scrolling */
}
</style>
